import { useQuery } from "@tanstack/react-query";

import { axiosGetData } from "@shared/api/clients";
import { usePaginatedQuery, useUrlPaginatedQuery } from "@shared/hooks";
import type { PaginatedQueryParams, PaginatedResult } from "@shared/api";

import {
  AdminStructureDamAnmClassificationResponse,
  AdminStructureDamRegionalClassification,
  AdminStructureDetail,
  AdminStructureFilters,
  AdminStructureRaisingHistoryFilters,
  AdminStructureReservoirManagementFilters,
  ListAdminStructureReservoirManagementsResponse,
  ListAdminStructureRaisingHistoriesResponse,
  ListAdminStructuresResponseItem,
} from "./structures.types";

export const ADMIN_STRUCTURES_QUERY_KEY = ["admin", "structures"];

export const ADMIN_STRUCTURES_FILTER_KEYS: (keyof AdminStructureFilters &
  string)[] = ["name", "type", "operationalStatus", "sortBy", "sortOrder"];

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

export const listAdminStructures = async (
  complexId: string,
  params?: PaginatedQueryParams<AdminStructureFilters>,
): Promise<PaginatedResult<ListAdminStructuresResponseItem>> => {
  const cleanParams = Object.fromEntries(
    Object.entries({
      page: DEFAULT_PAGE,
      limit: DEFAULT_LIMIT,
      ...params,
    }).filter(([, value]) => value !== undefined),
  );

  return axiosGetData<PaginatedResult<ListAdminStructuresResponseItem>>(
    `v1/admin/complexes/${complexId}/structures`,
    cleanParams,
  );
};

export const getAdminStructure = async (
  complexId: string,
  structureId: string,
): Promise<AdminStructureDetail> =>
  axiosGetData<AdminStructureDetail>(
    `v1/admin/complexes/${complexId}/structures/${structureId}`,
  );

export const useAdminStructureQuery = (
  complexId: string | null,
  structureId: string | null,
) =>
  useQuery({
    queryKey: [...ADMIN_STRUCTURES_QUERY_KEY, complexId, structureId, "detail"],
    queryFn: () => getAdminStructure(complexId!, structureId!),
    enabled: Boolean(complexId && structureId),
  });

export const useAdminStructuresQuery = (
  complexId: string | null,
  params?: PaginatedQueryParams<AdminStructureFilters>,
) => {
  return usePaginatedQuery<
    ListAdminStructuresResponseItem,
    AdminStructureFilters
  >({
    endpoint: complexId
      ? `v1/admin/complexes/${complexId}/structures`
      : "v1/admin/complexes/__missing__/structures",
    queryKeyBase: [...ADMIN_STRUCTURES_QUERY_KEY, complexId ?? "none"],
    params,
    enabled: Boolean(complexId),
    queryOptions: { staleTime: 0 },
  });
};

export const listAdminStructureRaisingHistories = async (
  complexId: string,
  structureId: string,
  params?: AdminStructureRaisingHistoryFilters,
): Promise<ListAdminStructureRaisingHistoriesResponse> => {
  const cleanParams = Object.fromEntries(
    Object.entries({
      page: DEFAULT_PAGE,
      limit: DEFAULT_LIMIT,
      ...params,
    }).filter(([, value]) => value !== undefined && value !== ""),
  );

  return axiosGetData<ListAdminStructureRaisingHistoriesResponse>(
    `v1/admin/complexes/${complexId}/structures/${structureId}/raising-histories`,
    cleanParams,
  );
};

export const useAdminStructureRaisingHistoriesQuery = (
  complexId: string,
  structureId: string,
  filters?: AdminStructureRaisingHistoryFilters,
) =>
  useQuery({
    queryKey: [
      ...ADMIN_STRUCTURES_QUERY_KEY,
      complexId,
      structureId,
      "raising-histories",
      filters ?? {},
    ],
    queryFn: () =>
      listAdminStructureRaisingHistories(complexId, structureId, filters),
    enabled: Boolean(complexId && structureId),
  });

export const listAdminStructureReservoirManagements = async (
  complexId: string,
  structureId: string,
  params?: AdminStructureReservoirManagementFilters,
): Promise<ListAdminStructureReservoirManagementsResponse> => {
  const cleanParams = Object.fromEntries(
    Object.entries({
      page: DEFAULT_PAGE,
      limit: DEFAULT_LIMIT,
      ...params,
    }).filter(([, value]) => value !== undefined && value !== ""),
  );

  return axiosGetData<ListAdminStructureReservoirManagementsResponse>(
    `v1/admin/complexes/${complexId}/structures/${structureId}/reservoir-managements`,
    cleanParams,
  );
};

export const useAdminStructureReservoirManagementsQuery = (
  complexId: string,
  structureId: string,
  filters?: AdminStructureReservoirManagementFilters,
) =>
  useQuery({
    queryKey: [
      ...ADMIN_STRUCTURES_QUERY_KEY,
      complexId,
      structureId,
      "reservoir-managements",
      filters ?? {},
    ],
    queryFn: () =>
      listAdminStructureReservoirManagements(complexId, structureId, filters),
    enabled: Boolean(complexId && structureId),
  });

export const getAdminStructureAnmClassification = async (
  complexId: string,
  structureId: string,
): Promise<AdminStructureDamAnmClassificationResponse | null> =>
  axiosGetData<AdminStructureDamAnmClassificationResponse | null>(
    `v1/admin/complexes/${complexId}/structures/${structureId}/anm-classification`,
  );

export const useAdminStructureAnmClassificationQuery = (
  complexId: string,
  structureId: string,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: [
      ...ADMIN_STRUCTURES_QUERY_KEY,
      complexId,
      structureId,
      "anm-classification",
    ],
    queryFn: () => getAdminStructureAnmClassification(complexId, structureId),
    enabled: options?.enabled ?? Boolean(complexId && structureId),
  });

export const getAdminStructureRegionalClassification = async (
  complexId: string,
  structureId: string,
): Promise<AdminStructureDamRegionalClassification | null> =>
  axiosGetData<AdminStructureDamRegionalClassification | null>(
    `v1/admin/complexes/${complexId}/structures/${structureId}/regional-classification`,
  );

export const useAdminStructureRegionalClassificationQuery = (
  complexId: string,
  structureId: string,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: [
      ...ADMIN_STRUCTURES_QUERY_KEY,
      complexId,
      structureId,
      "regional-classification",
    ],
    queryFn: () =>
      getAdminStructureRegionalClassification(complexId, structureId),
    enabled: options?.enabled ?? Boolean(complexId && structureId),
  });

export const useAdminStructuresUrlQuery = (complexId: string | null) => {
  return useUrlPaginatedQuery<
    ListAdminStructuresResponseItem,
    AdminStructureFilters
  >({
    endpoint: complexId
      ? `v1/admin/complexes/${complexId}/structures`
      : "v1/admin/complexes/__missing__/structures",
    queryKeyBase: [...ADMIN_STRUCTURES_QUERY_KEY, complexId ?? "none"],
    filterKeys: ADMIN_STRUCTURES_FILTER_KEYS,
    enabled: Boolean(complexId),
  });
};
